import React, { useCallback, useEffect, useState } from 'react';

import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_LOW,
  FORMAT_TEXT_COMMAND,
  SELECTION_CHANGE_COMMAND,
  TextFormatType,
} from 'lexical';
import { mergeRegister } from '@lexical/utils';

import { Box } from '../../../../Box';
import { Icon } from '../../../../Icon';
import { ToolbarButton } from '../common';

interface FormatTextPluginProps {
  bold: boolean;
  italic: boolean;
  underline: boolean;
  strikethrough: boolean;
  code: boolean;
  superscript: boolean;
  subscript: boolean;
}

export const FormatTextPlugin = ({
  bold,
  italic,
  underline,
  strikethrough,
  code,
  superscript,
  subscript,
}: FormatTextPluginProps) => {
  const [editor] = useLexicalComposerContext();

  const [isBold, setIsBold] = useState<boolean>(false);
  const [isItalic, setIsItalic] = useState<boolean>(false);
  const [isUnderline, setIsUnderline] = useState<boolean>(false);
  const [isStrikethrough, setIsStrikethrough] = useState<boolean>(false);
  const [isCode, setIsCode] = useState<boolean>(false);
  const [isSuperscript, setIsSuperscript] = useState<boolean>(false);
  const [isSubscript, setIsSubscript] = useState<boolean>(false);

  const getFormatColor = (value: boolean) =>
    value ? 'original.brand.primary' : 'text.light.primary';

  const handleFormatClick = (format: TextFormatType) => () => {
    editor.dispatchCommand(FORMAT_TEXT_COMMAND, format);
  };

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();

    if ($isRangeSelection(selection)) {
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      setIsUnderline(selection.hasFormat('underline'));
      setIsStrikethrough(selection.hasFormat('strikethrough'));
      setIsCode(selection.hasFormat('code'));
      setIsSuperscript(selection.hasFormat('superscript'));
      setIsSubscript(selection.hasFormat('subscript'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateToolbar();
        });
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateToolbar();
          return false;
        },
        COMMAND_PRIORITY_LOW,
      ),

      editor.registerCommand(
        FORMAT_TEXT_COMMAND,
        (payload) => {
          const selection = $getSelection();

          if ($isRangeSelection(selection)) {
            if (
              payload === 'underline' &&
              selection.hasFormat('strikethrough')
            ) {
              selection.formatText('strikethrough', undefined);
            }

            if (
              payload === 'strikethrough' &&
              selection.hasFormat('underline')
            ) {
              selection.formatText('underline', undefined);
            }

            if (payload === 'code') {
              if (selection.hasFormat('bold'))
                selection.formatText('bold', undefined);
              if (selection.hasFormat('italic'))
                selection.formatText('italic', undefined);
              if (selection.hasFormat('underline'))
                selection.formatText('underline', undefined);
              if (selection.hasFormat('strikethrough'))
                selection.formatText('strikethrough', undefined);
              if (selection.hasFormat('superscript'))
                selection.formatText('superscript', undefined);
              if (selection.hasFormat('subscript'))
                selection.formatText('subscript', undefined);
            }

            if (payload !== 'code' && selection.hasFormat('code')) {
              selection.formatText('code', undefined);
            }

            selection.formatText(payload);
          }
          return true;
        },
        1,
      ),
    );
  }, [editor, updateToolbar]);

  return (
    <Box display="flex" gap="2">
      {bold && (
        <ToolbarButton
          tooltip="Жирный текст"
          onClick={handleFormatClick('bold')}
        >
          <Icon
            name="text_bold_outline"
            size="16"
            color={getFormatColor(isBold)}
          />
        </ToolbarButton>
      )}
      {italic && (
        <ToolbarButton
          tooltip="Наклонный текст"
          onClick={handleFormatClick('italic')}
        >
          <Icon
            name="text_italic_outline"
            size="16"
            color={getFormatColor(isItalic)}
          />
        </ToolbarButton>
      )}
      {underline && (
        <ToolbarButton
          tooltip="Подчеркнутый текст"
          onClick={handleFormatClick('underline')}
        >
          <Icon
            name="text_underline_outline"
            size="16"
            color={getFormatColor(isUnderline)}
          />
        </ToolbarButton>
      )}
      {strikethrough && (
        <ToolbarButton
          tooltip="Зачеркнутый текст"
          onClick={handleFormatClick('strikethrough')}
        >
          <Icon
            name="text_strikethrough_outline"
            size="16"
            color={getFormatColor(isStrikethrough)}
          />
        </ToolbarButton>
      )}
      {code && (
        <ToolbarButton
          tooltip="Исходный код"
          onClick={handleFormatClick('code')}
        >
          <Icon name="vkhrtek_code" color={getFormatColor(isCode)} size="16" />
        </ToolbarButton>
      )}
      {superscript && (
        <ToolbarButton
          tooltip="Надстрочный текст"
          onClick={handleFormatClick('superscript')}
        >
          <Icon
            name="vkhrtek_superscript"
            color={getFormatColor(isSuperscript)}
            size="16"
          />
        </ToolbarButton>
      )}
      {subscript && (
        <ToolbarButton
          tooltip="Подстрочный текст"
          onClick={handleFormatClick('subscript')}
        >
          <Icon
            name="vkhrtek_subscript"
            color={getFormatColor(isSubscript)}
            size="16"
          />
        </ToolbarButton>
      )}
    </Box>
  );
};
